import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {Object} BundleOfferRefs
 * @property {HTMLInputElement[]} options - Bundle radios, one per quantity.
 * @property {HTMLElement[]} [prices] - Price containers, refreshed when the variant changes.
 */

/**
 * @typedef {HTMLElement & {
 *   getValue: () => string,
 *   setValue: (value: string) => void,
 *   updateButtonStates: () => void,
 *   onQuantityChange: () => void,
 * }} QuantitySelectorElement
 */

/**
 * "Bundle & save" picker. Choosing a bundle sets the product form quantity; changing the
 * quantity elsewhere re-selects the matching bundle. Tier prices are re-read from the
 * section render on variant change. The savings themselves are Shopify automatic discounts.
 *
 * @extends {Component<BundleOfferRefs>}
 */
class BundleOfferComponent extends Component {
  requiredRefs = ['options'];

  /** @type {AbortController | undefined} */
  #listeners;

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    const section = this.closest('.shopify-section, dialog');
    section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#syncFromQuantity, { signal });
    // The product form resets its quantity after adding to cart.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#syncFromQuantity, { signal });

    this.#syncFromQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
  }

  /**
   * Sets the product form quantity to the chosen bundle size.
   * @param {Event} event - Change event from a bundle radio.
   */
  selectQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const quantitySelector = this.#getQuantitySelector();
    if (!quantitySelector) return;

    quantitySelector.setValue(event.target.value);
    quantitySelector.updateButtonStates();
    quantitySelector.onQuantityChange();
  }

  /**
   * @returns {QuantitySelectorElement | null}
   */
  #getQuantitySelector() {
    const element = this.closest('.shopify-section, dialog')?.querySelector(
      'product-form-component quantity-selector-component'
    );

    if (!(element instanceof HTMLElement) || !('setValue' in element) || !('getValue' in element)) return null;

    return /** @type {QuantitySelectorElement} */ (element);
  }

  /**
   * Checks the bundle that matches the current form quantity, or none when it matches no bundle.
   */
  #syncFromQuantity = () => {
    // Wait a frame so updates made by the product form after the same event are settled.
    requestAnimationFrame(() => {
      const quantity = this.#getQuantitySelector()?.getValue();
      if (quantity === undefined) return;

      for (const option of this.refs.options ?? []) {
        option.checked = option.value === String(Number(quantity));
      }
    });
  };

  /**
   * Refreshes tier prices for the newly selected variant.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const html = detail?.html;
        if (!(html instanceof Document)) return;

        const next = html.querySelector(`bundle-offer-component[data-block-id="${this.dataset.blockId}"]`);
        const nextPrices = next?.querySelectorAll('[ref="prices[]"]') ?? [];

        (this.refs.prices ?? []).forEach((price, index) => {
          const nextPrice = nextPrices[index];
          if (nextPrice) price.innerHTML = nextPrice.innerHTML;
        });

        this.#syncFromQuantity();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[bundle-offer] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('bundle-offer-component')) {
  customElements.define('bundle-offer-component', BundleOfferComponent);
}
